"use client";
import "./globals.css";
import { LanguageProvider } from "@/utils/LanguageContext";
import CookieConsent from "@/component/common/CookieConsent";
import StructuredData from "@/component/common/StructuredData";
import Hotjar from "@/component/common/Hotjar";
import { usePathname } from "next/navigation";
import ReferralHandler from "@/component/ReferralHandler";
import Script from "next/script";

const baseUrl = (process.env.NEXT_PUBLIC_BASE_URL || 'https://invtron.com').replace(/\/$/, '');
const GOOGLE_ADS_ID = "AW-17756669852";
const GOOGLE_TAG_ID = "GT-M397VD8L";
const GTM_CONTAINER_ID = "GTM-TX2H8SL";

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname() || '/';

  const languages = ['en', 'ar', 'ko', 'ph', 'hi', 'ur', 'ru', 'id', 'zh', 'fr', 'es', 'it', 'de'] as const;
  const segment = pathname.split('/')[1];
  const lang = (languages as readonly string[]).includes(segment) ? segment : 'en';
  const pathWithoutLang = (languages as readonly string[]).includes(segment)
    ? pathname.replace(`/${segment}`, '') || '/'
    : pathname;
  const canonical = `${baseUrl}${pathname === '/' ? '' : pathname}`;

  const descriptions: Record<string, string> = {
    en: 'Invtron DAO is a revolutionary idea, changing the face of crowdfunding world and will bridge the gap between startups and investors using Blockchain Technology.',
    ar: 'إنفترون داو فكرة ثورية تغير وجه عالم التمويل الجماعي وستجسر الفجوة بين الشركات الناشئة والمستثمرين. سيتيح دمج منصتنا مستوى أعلى من الثقة من المستثمرين والشركات الناشئة.',
    es: 'Invtron DAO es una idea revolucionaria, cambiando la cara del mundo del crowdfunding y cerrando la brecha entre startups e inversores. La integración de nuestra plataforma permitirá un nivel más alto de confianza por parte de inversores y startups.',
    fr: 'Invtron DAO est une idée révolutionnaire qui transforme le crowdfunding et comble le fossé entre startups et investisseurs. L’intégration de notre plateforme offrira un niveau de confiance accru pour les investisseurs et les startups.',
    de: 'Invtron DAO ist eine revolutionaere Idee, die die Welt des Crowdfundings veraendert und die Luecke zwischen Start-ups und Investoren mit Blockchain-Technologie schliesst.',
    hi: 'इन्वट्रॉन डाओ एक क्रांतिकारी विचार है, जो क्राउडफंडिंग की दुनिया का चेहरा बदल रहा है और स्टार्टअप और निवेशकों के बीच की खाई को पाट देगा। हमारे प्लेटफॉर्म का एकीकरण निवेशकों और स्टार्टअप से उच्च स्तर के विश्वास की अनुमति देगा।',
    id: 'Invtron DAO adalah ide revolusioner, mengubah wajah dunia crowdfunding dan akan menjembatani kesenjangan antara startup dan investor. Integrasi platform kami memungkinkan tingkat kepercayaan yang lebih tinggi dari investor maupun startup.',
    it: "Invtron DAO è un'idea rivoluzionaria, che cambia il volto del mondo del crowdfunding e colmerà il divario tra startup e investitori. L'integrazione della nostra piattaforma consentirà un livello più elevato di fiducia da parte di investitori e startup.",
    ko: 'Invtron DAO는 크라우드펀딩의 판도를 바꾸어 스타트업과 투자자 간의 간극을 메우는 혁신적인 개념입니다. 우리 플랫폼이 통합되면 투자자와 스타트업 모두 더 높은 신뢰를 얻게 됩니다.',
    ph: 'Ang Invtron DAO ay isang rebolusyonaryong ideya, na nagbabago sa mukha ng mundo ng crowdfunding at magiging tulay sa pagitan ng mga startup at mga mamumuhunan. Ang pagsasama-sama ng aming platform ay magbibigay-daan para sa mas mataas na antas ng tiwala mula sa mga mamumuhunan at mga startup.',
    ru: 'Invtron DAO — революционная идея, меняющая облик краудфандинга и устраняющая разрыв между стартапами и инвесторами. Интеграция нашей платформы позволит инвесторам и стартапам обрести более высокий уровень доверия.',
    ur: 'انوٹرون ڈاؤ ایک انقلابی آئیڈیا ہے، جو کراؤڈ فنڈنگ کی دنیا کا چہرہ بدل رہا ہے اور اسٹارٹ اپس اور سرمایہ کاروں کے درمیان فرق کو ختم کرے گا۔ ہمارے پلیٹ فارم کا انضمام سرمایہ کاروں اور اسٹارٹ اپس کی جانب سے اعلیٰ سطح کے اعتماد کی اجازت دے گا۔',
    zh: 'Invtron DAO 是一种革命性理念，改变了众筹世界的面貌，弥合了初创企业与投资者之间的鸿沟。平台的整合将为投资者和初创企业带来更高的信心.',
  };

  const keywords: Record<string, string> = {
    en: 'invtron, crowdfunding, blockchain investment, startup funding, ico, crypto, decentralized finance, web3, defi, venture capital, fundraising platform',
    ar: 'انفترون, تمويل جماعي, استثمار بلوك تشين, تمويل الشركات الناشئة, ico, عملات رقمية, تمويل لامركزي, ويب3, ديفاي, رأس مال مغامر, منصة تمويل',
    fr: 'invtron, financement participatif, investissement blockchain, financement de start-up, ico, crypto, finance décentralisée, web3, defi, capital-risque, plateforme de financement',
    es: 'invtron, crowdfunding, inversión blockchain, financiación startup, ico, crypto, finanzas descentralizadas, web3, defi, capital riesgo, plataforma de financiación',
    de: 'invtron, crowdfunding, blockchain-investition, startup-finanzierung, ico, krypto, dezentrale finanzen, web3, defi, risikokapital, fundraising-plattform',
    ko: 'invtron, 크라우드펀딩, 블록체인 투자, 스타트업 자금, ico, 암호화폐, 탈중앙화 금융, web3, defi, 벤처 캐피탈, 자금 조달 플랫폼',
    ph: 'invtron, crowdfunding, blockchain investment, startup funding, ico, crypto, decentralized finance, web3, defi, venture capital, fundraising platform',
    hi: 'invtron, क्राउडफंडिंग, ब्लॉकचेन निवेश, स्टार्टअप फंडिंग, ico, क्रिप्टो, विकेंद्रीकृत वित्त, web3, defi, वेंचर कैपिटल, फंडरेजिंग प्लेटफॉर्म',
    ru: 'invtron, краудфандинг, блокчейн инвестиции, финансирование стартапов, ico, крипто, децентрализованные финансы, web3, defi, венчурный капитал, платформа финансирования',
    id: 'invtron, crowdfunding, investasi blockchain, pendanaan startup, ico, crypto, keuangan terdesentralisasi, web3, defi, modal ventura, platform pendanaan',
    ur: 'invtron, کراؤڈ فنڈنگ, بلاک چین سرمایہ کاری, اسٹارٹ اپ فنڈنگ, ico, کرپٹو, غیر مرکزی مالیات, web3, defi, وینچر کیپیٹل, فنڈ ریزنگ پلیٹ فارم',
    zh: 'invtron, 众筹, 区块链投资, 创业融资, ico, 加密货币, 去中心化金融, web3, defi, 风险投资, 融资平台',
    it: 'invtron, crowdfunding, investimento blockchain, finanziamento startup, ico, crypto, finanza decentralizzata, web3, defi, capitale di rischio, piattaforma di finanziamento',
  };

  const ogLocales: Record<string, string> = {
    en: 'en_US',
    ar: 'ar_AR',
    ko: 'ko_KR',
    ph: 'en_PH',
    hi: 'hi_IN',
    ur: 'ur_PK',
    ru: 'ru_RU',
    id: 'id_ID',
    zh: 'zh_CN',
    fr: 'fr_FR',
    es: 'es_ES',
    it: 'it_IT',
    de: 'de_DE',
  };

  const titles: Record<string, string> = {
    en: 'Invtron DAO | Crowdfunding Powered by Blockchain',
    ar: 'إنفترون داو | تمويل جماعي مدعوم بالبلوك تشين',
    es: 'Invtron DAO | Crowdfunding impulsado por blockchain',
    fr: 'Invtron DAO | Financement participatif propulsé par la blockchain',
    de: 'Invtron DAO | Blockchain-basiertes Crowdfunding',
    hi: 'Invtron DAO | ब्लॉकचेन द्वारा संचालित क्राउडफंडिंग',
    id: 'Invtron DAO | Crowdfunding didukung blockchain',
    it: 'Invtron DAO | Crowdfunding alimentato dalla blockchain',
    ko: 'Invtron DAO | 블록체인 기반 크라우드펀딩',
    ph: 'Invtron DAO | Crowdfunding na pinapagana ng blockchain',
    ru: 'Invtron DAO | Краудфандинг на блокчейне',
    ur: 'انفترون ڈاؤ | بلاک چین سے طاقت یافتہ کراؤڈ فنڈنگ',
    zh: 'Invtron DAO | 基于区块链的众筹',
  };

  const siteNames: Record<string, string> = {
    en: 'Invtron DAO',
    ar: 'إنفترون داو',
    es: 'Invtron DAO',
    fr: 'Invtron DAO',
    de: 'Invtron DAO',
    hi: 'Invtron DAO',
    id: 'Invtron DAO',
    it: 'Invtron DAO',
    ko: 'Invtron DAO',
    ph: 'Invtron DAO',
    ru: 'Invtron DAO',
    ur: 'انفترون ڈاؤ',
    zh: 'Invtron DAO',
  };

  const imageAlts: Record<string, string> = {
    en: 'Invtron DAO logo',
    ar: 'شعار إنفترون داو',
    es: 'Logotipo de Invtron DAO',
    fr: "Logo d'Invtron DAO",
    de: 'Invtron DAO Logo',
    hi: 'इन्वट्रॉन डाओ लोगो',
    id: 'Logo Invtron DAO',
    it: 'Logo di Invtron DAO',
    ko: 'Invtron DAO 로고',
    ph: 'Logo ng Invtron DAO',
    ru: 'Логотип Invtron DAO',
    ur: 'انفترون ڈاؤ کا لوگو',
    zh: 'Invtron DAO 徽标',
  };

  const description = descriptions[lang] || descriptions.en;
  const keyword = keywords[lang] || keywords.en;
  const ogLocale = ogLocales[lang] || 'en_US';
  const title = titles[lang] || titles.en;
  const siteName = siteNames[lang] || siteNames.en;
  const imageAlt = imageAlts[lang] || imageAlts.en;
  const rtlLang = lang === 'ar' || lang === 'ur';

  return (
    <html
      lang={lang}
      dir={rtlLang ? 'rtl' : 'ltr'}
      data-scroll-behavior="smooth"
      suppressHydrationWarning
    >
      <head>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="description" content={description} />
        <meta name="keywords" content={keyword} />
        <meta name="robots" content="index, follow" />
        <meta
          httpEquiv="Content-Language" content={lang} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={canonical} />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${baseUrl}${pathWithoutLang === '/' ? '' : pathWithoutLang}`}
        />
        {languages.map((l) => (
          <link
            key={l}
            rel="alternate"
            hrefLang={l}
            href={`${baseUrl}/${l}${pathWithoutLang === '/' ? '' : pathWithoutLang}`}
          />
        ))}
        <meta property="og:title" content={title} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${baseUrl}/favicon.png`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:image:alt" content={imageAlt} />
        {Object.entries(ogLocales)
          .filter(([l]) => l !== lang)
          .map(([l, locale]) => (
            <meta key={l} property="og:locale:alternate" content={locale} />
          ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${baseUrl}/favicon.png`} />
        <meta name="twitter:site" content="@InvtronDAO" />
        <meta name="twitter:creator" content="@InvtronDAO" />
        <meta name="twitter:image:alt" content={imageAlt} />
        <meta name="author" content={siteName} />
        <meta name="theme-color" content="#ffffff" />
        <link rel="icon" href="/favicon.png" sizes="any" />
        <StructuredData />
        <Hotjar />
        <Script id="gtm-init" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          `}
        </Script>
        <Script
          id="gtm-script"
          src={`https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`}
          strategy="afterInteractive"
        />
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
          strategy="afterInteractive"
        />
        <Script id="gtag-init" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GOOGLE_ADS_ID}');
            gtag('config', '${GOOGLE_TAG_ID}');
          `}
        </Script>
      </head>
      <body suppressHydrationWarning={true}>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        <LanguageProvider>
          <ReferralHandler />
          {children}
          <CookieConsent />
        </LanguageProvider>
      </body>
    </html>
  );
}
